import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { User, Hotel, Check } from "lucide-react";

export default function Onboarding() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState(null);
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user?.role) {
    navigate("/", { replace: true });
    return null;
  }

  const submit = async () => {
    if (!role) return toast.error("Pick a role first");
    setSubmitting(true);
    try {
      await api.post("/auth/role", { role, phone });
      await refresh();
      toast.success("Welcome aboard!");
      navigate(role === "hotel_partner" ? "/partner" : "/customer");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not set role");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-slate-50 py-16">
      <div className="max-w-3xl mx-auto px-4">
        <div className="hw-label text-blue-700">One last step</div>
        <h1 className="hw-head text-4xl md:text-5xl font-black mt-3 text-slate-900">How will you use SwaanStay?</h1>
        <p className="text-slate-600 mt-3">Pick the role that fits. You can&apos;t change this later (admins only).</p>

        <div className="grid md:grid-cols-2 gap-5 mt-10">
          <button
            data-testid="role-customer-btn"
            onClick={() => setRole("customer")}
            className={`text-left p-7 hw-card transition-all hover:-translate-y-0.5 ${role === "customer" ? "border-blue-600 hw-shadow-pop-amber" : "hover:hw-shadow-pop"}`}
          >
            <div className="flex items-center justify-between">
              <User className="w-6 h-6 text-slate-900" />
              {role === "customer" && <Check className="w-5 h-5 text-emerald-600" />}
            </div>
            <h3 className="hw-head text-2xl font-black mt-6 text-slate-900">I&apos;m a Traveller</h3>
            <p className="text-sm text-slate-600 mt-2">Search hotels, negotiate prices, post group requests for weddings, events & corporate stays.</p>
          </button>
          <button
            data-testid="role-partner-btn"
            onClick={() => setRole("hotel_partner")}
            className={`text-left p-7 hw-card transition-all hover:-translate-y-0.5 ${role === "hotel_partner" ? "border-blue-600 hw-shadow-pop-amber" : "hover:hw-shadow-pop"}`}
          >
            <div className="flex items-center justify-between">
              <Hotel className="w-6 h-6 text-slate-900" />
              {role === "hotel_partner" && <Check className="w-5 h-5 text-emerald-600" />}
            </div>
            <h3 className="hw-head text-2xl font-black mt-6 text-slate-900">I&apos;m a Hotel Partner</h3>
            <p className="text-sm text-slate-600 mt-2">List my hotel, receive leads, submit competitive quotes, close direct bookings.</p>
          </button>
        </div>

        <div className="mt-8 hw-card p-6">
          <label className="hw-label text-slate-600">Phone number (optional)</label>
          <Input
            data-testid="onboarding-phone-input"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+91 9XXXXXXXXX"
            className="mt-2 rounded-sm border-slate-300"
          />
          <Button
            onClick={submit}
            disabled={!role || submitting}
            data-testid="onboarding-submit-btn"
            className="w-full mt-5 bg-emerald-600 hover:bg-emerald-700 text-slate-900 rounded-sm font-bold py-5 h-auto"
          >
            {submitting ? "Setting up…" : "Continue"}
          </Button>
        </div>
      </div>
    </div>
  );
}
